/**
 * RESPONSIBILITY: the words the interface itself says, on every page.
 *
 * Not a page's content — the page's own section holds that. This is the handful of
 * strings that belong to no page because they appear on all of them: the trail back to
 * the top, the count above a filtered list, the line shown when a filter matches
 * nothing. Every one of them used to be a literal inside a component, which meant the
 * one kind of change a church actually makes often — the wording — was the one kind
 * that needed a developer.
 *
 * Page names sit here too, and only once. A breadcrumb naming the events page and the
 * events page itself must agree, and they cannot if each is typed where it is used.
 *
 * No @EditableBy, so WRITE defaults to NONE — admin only.
 */
import { Section, Text } from '@framework/core/decorators';
import { SectionName } from '../section-names';

const LISTS = 'Lists and filters';
const NAMES = 'Page names';

@Section({ name: SectionName.Words, label: 'Site · Wording' })
export class WordsSection {
  @Text({
    label: 'Contact requests', maxLength: 120, default: 'Contact requests', group: NAMES,
    description: 'The admin menu entry that opens the list of messages people sent through the contact form.',
  })
  requestsLabel!: string;

  @Text({
    label: 'Home', maxLength: 120, default: 'Home', group: 'Breadcrumbs',
    description: 'First step of the trail at the top of every page below the front one.',
  })
  homeLabel!: string;

  @Text({
    label: 'Back to the front page', maxLength: 120, default: '← Back to home',
    group: 'Breadcrumbs',
  })
  backLabel!: string;

  @Text({
    label: 'Everything', maxLength: 120, default: 'All', group: LISTS,
    description: 'The tag that clears the other tags.',
  })
  allLabel!: string;

  @Text({
    label: 'How many are shown', maxLength: 300, default: 'Showing {shown} of {total}',
    group: LISTS,
    description: 'Write {shown} and {total} where the two numbers should appear.',
  })
  countTemplate!: string;

  @Text({
    label: 'Nothing matched', maxLength: 600,
    default: 'Nothing matches that. Try clearing the filters.', group: LISTS,
  })
  noMatchLabel!: string;

  @Text({ label: 'Show more', maxLength: 120, default: 'Show more', group: LISTS })
  showMoreLabel!: string;

  @Text({ label: 'Clear', maxLength: 120, default: 'Clear', group: LISTS })
  clearLabel!: string;

  @Text({
    label: 'Not yet public', maxLength: 120, default: 'Not shown to visitors', group: LISTS,
    description: 'Marks a row only you can see, because no date has published it yet.',
  })
  notPublicLabel!: string;

  @Text({ label: 'From a date', maxLength: 120, default: 'From', group: LISTS })
  fromLabel!: string;

  @Text({ label: 'Until a date', maxLength: 120, default: 'To', group: LISTS })
  toLabel!: string;

  @Text({
    label: 'About', maxLength: 120, default: 'About', group: NAMES, collapsed: true,
    description: 'These name the pages in the trail at the top. Change one here and every trail follows.',
  })
  aboutLabel!: string;

  @Text({ label: 'Leadership', maxLength: 120, default: 'Leadership', group: NAMES })
  leadershipLabel!: string;

  @Text({ label: 'Contact', maxLength: 120, default: 'Contact Us', group: NAMES })
  contactLabel!: string;

  @Text({ label: 'Events', maxLength: 120, default: 'Events', group: NAMES })
  eventsLabel!: string;

  @Text({ label: 'Ministries', maxLength: 120, default: 'Ministries', group: NAMES })
  ministriesLabel!: string;

  @Text({ label: 'Sermons', maxLength: 120, default: 'Sermons', group: NAMES })
  sermonsLabel!: string;

  @Text({ label: 'Children', maxLength: 120, default: 'Children', group: NAMES })
  childrenLabel!: string;

  @Text({ label: 'Give', maxLength: 120, default: 'Give', group: NAMES })
  giveLabel!: string;

  @Text({ label: 'Not found', maxLength: 120, default: 'Not found', group: NAMES })
  notFoundLabel!: string;

  @Text({
    label: 'Events filter', maxLength: 120, default: 'Filter events by tag',
    description:
      'Names the row of tag buttons for somebody using a screen reader. Not shown on '
      + 'the page.',
  })
  filterLabel!: string;
}
